import {
  messageSchema,
  MessageMarkdownTransformer,
  MessageMarkdownSerializer,
  Selection,
} from '@chatwoot/prosemirror-schema';
import { replaceVariablesInMessage } from '@chatwoot/utils';
import * as Sentry from '@sentry/vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

/**
 * The delimiter used to separate the signature from the rest of the body.
 * @type {string}
 */
export const SIGNATURE_DELIMITER = '--';

/**
 * Where the signature sits in the body.
 * - `footer`: email convention, `body\n\n--\n\nsignature`
 * - `prefix`: chat convention, `**signature**\nbody`
 *
 * The prefix uses a single line break on purpose: it is how WhatsApp itself
 * prints the sender name above a message in a group, with nothing in between.
 * A blank line would read as two separate messages.
 */
export const SIGNATURE_PLACEMENT = {
  FOOTER: 'footer',
  PREFIX: 'prefix',
};

/**
 * Joins the lines of a multiline signature when it is used as a chat prefix.
 * Bold does not survive a line break, neither in markdown nor on WhatsApp.
 * @type {string}
 */
export const SIGNATURE_PREFIX_JOINER = ' · ';

/**
 * Decides where the signature goes for a given channel.
 * Email keeps the `--` footer, every chat channel gets the bold prefix.
 *
 * @param {string} channelType - The channel type, eg. `Channel::Whatsapp`.
 * @returns {string} - One of SIGNATURE_PLACEMENT.
 */
export function signaturePlacement(channelType) {
  return channelType === INBOX_TYPES.EMAIL
    ? SIGNATURE_PLACEMENT.FOOTER
    : SIGNATURE_PLACEMENT.PREFIX;
}

/**
 * Parse and Serialize the markdown text to remove any extra spaces or new lines
 */
export function cleanSignature(signature) {
  try {
    // remove any horizontal rule tokens
    signature = signature
      .replace(/^( *\* *){3,} *$/gm, '')
      .replace(/^( *- *){3,} *$/gm, '')
      .replace(/^( *_ *){3,} *$/gm, '');

    const nodes = new MessageMarkdownTransformer(messageSchema).parse(
      signature
    );
    return MessageMarkdownSerializer.serialize(nodes);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(e);
    Sentry.captureException(e);
    // The parser can break on some cases
    // for example, Token type `hr` not supported by Markdown parser
    return signature;
  }
}

/**
 * Adds the signature delimiter to the beginning of the signature.
 *
 * @param {string} signature - The signature to add the delimiter to.
 * @returns {string} - The signature with the delimiter added.
 */
function appendDelimiter(signature) {
  return `${SIGNATURE_DELIMITER}\n\n${cleanSignature(signature)}`;
}

/**
 * Joins a multiline signature into a single line.
 *
 * Bold does not survive a line break, neither in markdown nor on WhatsApp:
 * `**line1\nline2**` renders the asterisks instead of the bold text.
 *
 * @param {string} signature - The raw signature.
 * @returns {string} - The signature on a single line, or an empty string.
 */
export function joinSignatureLines(signature) {
  if (!signature) return '';

  return (
    cleanSignature(signature)
      .split('\n')
      // a hard break is serialized as a trailing backslash by the markdown serializer
      .map(line => line.replace(/\\$/, '').trim())
      .filter(Boolean)
      .join(SIGNATURE_PREFIX_JOINER)
  );
}

/**
 * Builds the single line, bold signature used as a prefix on chat channels.
 * A signature that is already bold is left alone, `****text****` renders asterisks too.
 *
 * The rich editor renders `**bold**` as actual bold, so the agent never sees the
 * markup. The plain editor shows whatever is in the box, so there it uses
 * WhatsApp's own `*bold*` — one asterisk, the syntax agents already type by hand.
 * The server keeps it as is on the way out (`convert_markdown_emphasis`).
 *
 * @param {string} signature - The raw signature.
 * @param {Object} [options]
 * @param {boolean} [options.plain] - True when the composer shows raw text.
 * @returns {string} - The signature ready to be used as a prefix, or an empty string.
 */
export function buildPrefixSignature(signature, { plain = false } = {}) {
  const singleLine = joinSignatureLines(signature);

  if (!singleLine) return '';

  const isAlreadyBold = /\*\*|__/.test(singleLine);
  if (isAlreadyBold) return singleLine;

  return plain ? `*${singleLine}*` : `**${singleLine}**`;
}

/**
 * Every shape a prefix signature can have in a body, most specific first.
 * The composer can be switched between rich and plain with a signature already
 * in the box, so a body written in one shape has to be recognised in the other.
 */
function prefixSignatureVariants(signature) {
  return [
    buildPrefixSignature(signature),
    buildPrefixSignature(signature, { plain: true }),
  ].filter(Boolean);
}

/**
 * Check if there's an unedited signature in the body
 * If there is, return the index of the signature, If there isn't, return -1
 *
 * @param {string} body - The body to search for the signature.
 * @param {string} signature - The signature to search for.
 * @param {string} placement - Where the signature is expected, see SIGNATURE_PLACEMENT.
 * @returns {number} - The index of the occurrence of the signature in the body, or -1 if not found.
 */
export function findSignatureInBody(
  body,
  signature,
  placement = SIGNATURE_PLACEMENT.FOOTER
) {
  if (placement === SIGNATURE_PLACEMENT.PREFIX) {
    const trimmedBody = body.trimStart();
    const match = prefixSignatureVariants(signature).find(variant =>
      trimmedBody.startsWith(variant)
    );

    return match ? body.indexOf(match) : -1;
  }

  const trimmedBody = body.trimEnd();
  const cleanedSignature = cleanSignature(signature);

  // check if body ends with signature
  if (trimmedBody.endsWith(cleanedSignature)) {
    return body.lastIndexOf(cleanedSignature);
  }

  return -1;
}

/**
 * Adds the signature to the body, either as an email footer or as a chat prefix.
 *
 * @param {string} body - The body to add the signature to.
 * @param {string} signature - The signature to add.
 * @param {string} placement - Where the signature goes, see SIGNATURE_PLACEMENT.
 * @param {Object} [options]
 * @param {boolean} [options.plain] - True when the composer shows raw text.
 * @returns {string} - The body with the signature added.
 */
export function appendSignature(
  body,
  signature,
  placement = SIGNATURE_PLACEMENT.FOOTER,
  { plain = false } = {}
) {
  if (placement === SIGNATURE_PLACEMENT.PREFIX) {
    const prefixSignature = buildPrefixSignature(signature, { plain });
    if (!prefixSignature) return body;
    // if signature is already present, return body
    if (findSignatureInBody(body, signature, placement) > -1) {
      return body;
    }

    return `${prefixSignature}\n${body.trimStart()}`;
  }

  const cleanedSignature = cleanSignature(signature);
  // if signature is already present, return body
  if (findSignatureInBody(body, cleanedSignature) > -1) {
    return body;
  }

  return `${body.trimEnd()}\n\n${appendDelimiter(cleanedSignature)}`;
}

/**
 * Removes the signature used as a chat prefix, along with the line break after it.
 *
 * The editor writes that break back as a markdown hard break — two trailing
 * spaces before the newline — so those have to come off with it.
 *
 * @param {string} body - The body to remove the signature from.
 * @param {string} signature - The signature to remove.
 * @returns {string} - The body with the prefix signature removed.
 */
function removePrefixSignature(body, signature) {
  const trimmedBody = body.trimStart();
  const prefixSignature = prefixSignatureVariants(signature).find(variant =>
    trimmedBody.startsWith(variant)
  );

  if (!prefixSignature) return body;

  return trimmedBody
    .substring(prefixSignature.length)
    .replace(/^[^\S\r\n]*\r?\n/, '');
}

/**
 * Removes the signature from the body, along with the signature delimiter.
 *
 * Both placements are tried regardless of the channel: a draft saved before the
 * chat prefix existed still carries the `--` footer, and it has to come off too.
 *
 * @param {string} body - The body to remove the signature from.
 * @param {string} signature - The signature to remove.
 * @returns {string} - The body with the signature removed.
 */
export function removeSignature(body, signature) {
  body = removePrefixSignature(body, signature);

  // this will find the index of the signature if it exists
  // Regardless of extra spaces or new lines after the signature, the index will be the same if present
  const cleanedSignature = cleanSignature(signature);
  const signatureIndex = findSignatureInBody(body, cleanedSignature);

  // no need to trim the ends here, because it will simply be removed in the next method
  let newBody = body;

  // if signature is present, remove it and trim it
  // trimming will ensure any spaces or new lines before the signature are removed
  // This means we will have the delimiter at the end
  if (signatureIndex > -1) {
    newBody = newBody.substring(0, signatureIndex).trimEnd();
  }

  // let's find the delimiter and remove it
  const delimiterIndex = newBody.lastIndexOf(SIGNATURE_DELIMITER);
  if (
    delimiterIndex !== -1 &&
    delimiterIndex === newBody.length - SIGNATURE_DELIMITER.length // this will ensure the delimiter is at the end
  ) {
    // if the delimiter is at the end, remove it
    newBody = newBody.substring(0, delimiterIndex);
  }

  // return the value
  return newBody;
}

/**
 * Replaces the old signature with the new signature.
 * If the old signature is not present, it will append the new signature.
 *
 * @param {string} body - The body to replace the signature in.
 * @param {string} oldSignature - The signature to replace.
 * @param {string} newSignature - The signature to replace the old signature with.
 * @param {string} placement - Where the new signature goes, see SIGNATURE_PLACEMENT.
 * @param {Object} [options]
 * @param {boolean} [options.plain] - True when the composer shows raw text.
 * @returns {string} - The body with the old signature replaced with the new signature.
 *
 */
export function replaceSignature(
  body,
  oldSignature,
  newSignature,
  placement = SIGNATURE_PLACEMENT.FOOTER,
  { plain = false } = {}
) {
  const withoutSignature = removeSignature(body, oldSignature);
  return appendSignature(withoutSignature, newSignature, placement, { plain });
}

/**
 * Extract text from markdown, and remove all images, code blocks, links, headers, bold, italic, lists etc.
 * Links will be converted to text, and not removed.
 *
 * @param {string} markdown - markdown text to be extracted
 * @returns
 */
export function extractTextFromMarkdown(markdown) {
  return markdown
    .replace(/```[\s\S]*?```/g, '') // Remove code blocks
    .replace(/`.*?`/g, '') // Remove inline code
    .replace(/!\[.*?\]\(.*?\)/g, '') // Remove images before removing links
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove links but keep the text
    .replace(/#+\s*|[*_-]{1,3}/g, '') // Remove headers, bold, italic, lists etc.
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .join('\n') // Trim each line & remove any lines only having spaces
    .replace(/\n{2,}/g, '\n') // Remove multiple consecutive newlines (blank lines)
    .trim(); // Trim any extra space
}

/**
 * Opens the message line right below a prefix signature and parks the cursor there.
 *
 * The signature and the message share one paragraph, so that they end up on
 * consecutive lines with nothing in between. That makes the bold of the
 * signature the mark in force at the cursor, and everything the agent typed
 * would come out bold. Three details keep that from happening, and all three
 * have to stay in this single transaction:
 *
 * - the break is inserted with `inheritMarks` off, so the break node itself
 *   does not carry the bold — otherwise the mark comes back the moment the
 *   agent clicks away and clicks back
 * - the stored marks are cleared for the typing that follows
 * - the selection is set here; a later transaction that only moves the
 *   selection resets the stored marks and brings the bold back
 *
 * @param {EditorView} editorView - The Prosemirror EditorView
 */
export function startMessageLineAfterSignature(editorView) {
  if (!editorView) return;

  const { tr, schema } = editorView.state;

  tr.setSelection(Selection.atEnd(tr.doc));
  tr.replaceSelectionWith(schema.nodes.hard_break.create(), false);
  tr.setStoredMarks([]);

  editorView.dispatch(tr);
  editorView.focus();
}

/**
 * Scrolls the editor view into current cursor position
 *
 * @param {EditorView} view - The Prosemirror EditorView
 *
 */
export const scrollCursorIntoView = view => {
  // Get the current selection's head position (where the cursor is).
  const pos = view.state.selection.head;

  // Get the corresponding DOM node for that position.
  const domAtPos = view.domAtPos(pos);
  const node = domAtPos.node;

  // Scroll the node into view.
  if (node && node.scrollIntoView) {
    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
};

/**
 * Returns a transaction that inserts a node into editor at the given position
 * Has an optional param 'content' to check if the
 *
 * @param {Node} node - The prosemirror node that needs to be inserted into the editor
 * @param {number} from - Position in the editor where the node needs to be inserted
 * @param {number} to - Position in the editor where the node needs to be replaced
 *
 */
export function insertAtCursor(editorView, node, from, to) {
  if (!editorView) {
    return undefined;
  }

  // This is a workaround to prevent inserting content into new line rather than on the exiting line
  // If the node is of type 'doc' and has only one child which is a paragraph,
  // then extract its inline content to be inserted as inline.
  const isWrappedInParagraph =
    node.type.name === 'doc' &&
    node.childCount === 1 &&
    node.firstChild.type.name === 'paragraph';

  if (isWrappedInParagraph) {
    node = node.firstChild.content;
  }

  let tr;
  if (to) {
    tr = editorView.state.tr.replaceWith(from, to, node).insertText(` `);
  } else {
    tr = editorView.state.tr.insert(from, node);
  }
  const state = editorView.state.apply(tr);
  editorView.updateState(state);
  editorView.focus();

  return state;
}

/**
 * Determines the appropriate node and position to insert an image in the editor.
 *
 * Based on the current editor state and the provided image URL, this function finds out the correct node (either
 * a standalone image node or an image wrapped in a paragraph) and its respective position in the editor.
 *
 * 1. If the current node is a paragraph and doesn't contain an image or text, the image is inserted directly into it.
 * 2. If the current node isn't a paragraph or it's a paragraph containing text, the image will be wrapped
 *    in a new paragraph and then inserted.
 * 3. If the current node is a paragraph containing an image, the new image will be inserted directly into it.
 *
 * @param {Object} editorState - The current state of the editor. It provides necessary details like selection, schema, etc.
 * @param {string} fileUrl - The URL of the image to be inserted into the editor.
 * @returns {Object|null} An object containing details about the node to be inserted and its position. It returns null if no image node can be created.
 * @property {Node} node - The ProseMirror node to be inserted (either an image node or a paragraph containing the image).
 * @property {number} pos - The position where the new node should be inserted in the editor.
 */

export const findNodeToInsertImage = (editorState, fileUrl) => {
  const { selection, schema } = editorState;
  const { nodes } = schema;
  const currentNode = selection.$from.node();
  const {
    type: { name: typeName },
    content: { size, content },
  } = currentNode;

  const imageNode = nodes.image.create({ src: fileUrl });

  if (!imageNode) return null;

  const isInParagraph = typeName === 'paragraph';
  const needsNewLine =
    !content.some(n => n.type.name === 'image') && size !== 0 ? 1 : 0;

  return {
    node: isInParagraph ? imageNode : nodes.paragraph.create({}, imageNode),
    pos: selection.from + needsNewLine,
  };
};

/**
 * Set URL with query and size.
 *
 * @param {Object} selectedImageNode - The current selected node.
 * @param {Object} size - The size to set.
 * @param {Object} editorView - The editor view.
 */
export function setURLWithQueryAndSize(selectedImageNode, size, editorView) {
  if (selectedImageNode) {
    // Create and apply the transaction
    const tr = editorView.state.tr.setNodeMarkup(
      editorView.state.selection.from,
      null,
      {
        src: selectedImageNode.src,
        height: size.height,
      }
    );

    if (tr.docChanged) {
      editorView.dispatch(tr);
    }
  }
}

/**
 * Content Node Creation Helper Functions for
 * - mention
 * - canned response
 * - variable
 * - emoji
 */

/**
 * Centralized node creation function that handles the creation of different types of nodes based on the specified type.
 * @param {Object} editorView - The editor view instance.
 * @param {string} nodeType - The type of node to create ('mention', 'cannedResponse', 'variable', 'emoji').
 * @param {Object|string} content - The content needed to create the node, which varies based on node type.
 * @returns {Object|null} - The created ProseMirror node or null if the type is not supported.
 */
const createNode = (editorView, nodeType, content) => {
  const { state } = editorView;
  switch (nodeType) {
    case 'mention': {
      const mentionType = content.type || 'user';
      const displayName = content.displayName || content.name;

      const mentionNode = state.schema.nodes.mention.create({
        userId: content.id,
        userFullName: displayName,
        mentionType,
      });

      return mentionNode;
    }
    case 'cannedResponse':
      return new MessageMarkdownTransformer(messageSchema).parse(content);
    case 'variable':
      return state.schema.text(`{{${content}}}`);
    case 'emoji':
      return state.schema.text(content);
    case 'tool': {
      return state.schema.nodes.tools.create({
        id: content.id,
        name: content.title,
      });
    }
    default:
      return null;
  }
};

/**
 * Object mapping types to their respective node creation functions.
 */
const nodeCreators = {
  mention: (editorView, content, from, to) => ({
    node: createNode(editorView, 'mention', content),
    from,
    to,
  }),
  cannedResponse: (editorView, content, from, to, variables) => {
    const updatedMessage = replaceVariablesInMessage({
      message: content,
      variables,
    });
    const node = createNode(editorView, 'cannedResponse', updatedMessage);
    return {
      node,
      from: node.textContent === updatedMessage ? from : from - 1,
      to,
    };
  },
  variable: (editorView, content, from, to) => ({
    node: createNode(editorView, 'variable', content),
    from,
    to,
  }),
  emoji: (editorView, content, from, to) => ({
    node: createNode(editorView, 'emoji', content),
    from,
    to,
  }),
  tool: (editorView, content, from, to) => ({
    node: createNode(editorView, 'tool', content),
    from,
    to,
  }),
};

/**
 * Retrieves a content node based on the specified type and content, using a functional approach to select the appropriate node creation function.
 * @param {Object} editorView - The editor view instance.
 * @param {string} type - The type of content node to create ('mention', 'cannedResponse', 'variable', 'emoji').
 * @param {string|Object} content - The content to be transformed into a node.
 * @param {Object} range - An object containing 'from' and 'to' properties indicating the range in the document where the node should be placed.
 * @param {Object} variables - Optional. Variables to replace in the content, used for 'cannedResponse' type.
 * @returns {Object} - An object containing the created node and the updated 'from' and 'to' positions.
 */
export const getContentNode = (
  editorView,
  type,
  content,
  { from, to },
  variables
) => {
  const creator = nodeCreators[type];
  return creator
    ? creator(editorView, content, from, to, variables)
    : { node: null, from, to };
};
